import { Clock, ExternalLink, BarChart3, MessageSquareText } from "lucide-react";
import { Link } from "react-router-dom";
import type { ComparisonListItem } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { formatModelLabel } from "@/lib/format";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";

interface HistoryTableProps {
  comparisons: ComparisonListItem[];
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export function HistoryTable({ comparisons }: HistoryTableProps) {
  if (comparisons.length === 0) {
    return (
      <EmptyState
        icon={Clock}
        title="No comparisons yet"
        description="Run your first comparison to see results here."
      />
    );
  }

  return (
    <Card className="overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Prompt</TableHead>
            <TableHead>Model</TableHead>
            <TableHead>Winner</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {comparisons.map((comp) => (
            <TableRow key={comp.id}>
              <TableCell className="text-slate-500 whitespace-nowrap text-[13px]">
                {formatDate(comp.created_at)}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {comp.prompt_type === "sentiment" ? (
                    <MessageSquareText className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                  ) : (
                    <BarChart3 className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                  )}
                  <span className="font-medium text-brand-950 text-[13px]">{comp.prompt_name}</span>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="muted">{formatModelLabel(comp.model)}</Badge>
              </TableCell>
              <TableCell className="text-[13px]">
                {comp.winner ? (
                  <span className="font-medium text-brand-950">
                    {comp.winner}
                  </span>
                ) : (
                  <span className="text-slate-300">--</span>
                )}
              </TableCell>
              <TableCell>
                <StatusBadge status={comp.status} />
              </TableCell>
              <TableCell>
                {comp.report_id ? (
                  <Button variant="ghost" size="sm" asChild>
                    <Link to={`/app/reports/${comp.report_id}`} title={`Open report${comp.report_name ? `: ${comp.report_name}` : ""}`}>
                      Report
                    </Link>
                  </Button>
                ) : (
                  comp.status === "completed" && (
                    <Button variant="ghost" size="icon" asChild>
                      <Link to={`/app/compare/results/${comp.id}`} title="Open research brief">
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                    </Button>
                  )
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
